import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  LineChart, Line, CartesianGrid, Legend, Cell
} from 'recharts';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import { 
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, 
  Calendar, RefreshCw, ArrowUpRight, ArrowDownRight,
  Star, AlertTriangle, Lightbulb, ChevronDown, ChevronUp,
  Award, Target, Minus, BarChart3, Package
} from 'lucide-react';

const COLORS = ['#10b981', '#f97316', '#6366f1', '#ec4889', '#3b82f6', '#eab308'];
const MONTHS = [
  { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' }
];

const SalesComparison = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [productTrends, setProductTrends] = useState(null);
  
  // Comparison selection
  const currentDate = new Date();
  const [month1, setMonth1] = useState({ 
    year: currentDate.getFullYear(), 
    month: currentDate.getMonth() + 1 
  });
  const [month2, setMonth2] = useState({ 
    year: currentDate.getMonth() === 0 ? currentDate.getFullYear() - 1 : currentDate.getFullYear(), 
    month: currentDate.getMonth() === 0 ? 12 : currentDate.getMonth()
  });
  
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedProducts, setExpandedProducts] = useState(false);

  useEffect(() => {
    fetchSummary();
    fetchProductTrends();
  }, []);

  useEffect(() => {
    fetchComparison();
  }, [month1, month2]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/sales-comparison/monthly-summary?months=12&t=${Date.now()}`);
      setSummaryData(res.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
      toast.error('Error al cargar resumen');
    } finally {
      setLoading(false);
    }
  };

  const fetchComparison = async () => {
    try {
      const res = await api.get(
        `/reports/sales-comparison/compare?year1=${month1.year}&month1=${month1.month}&year2=${month2.year}&month2=${month2.month}&t=${Date.now()}`
      );
      setComparisonData(res.data);
    } catch (error) {
      console.error('Error fetching comparison:', error);
    }
  };

  const fetchProductTrends = async () => {
    try {
      const res = await api.get(`/reports/sales-comparison/product-trends?months=6&t=${Date.now()}`);
      setProductTrends(res.data);
    } catch (error) {
      console.error('Error fetching product trends:', error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', { 
      style: 'currency', 
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const getYears = () => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear - 1, currentYear - 2];
  };

  const getTrendIcon = (pct) => {
    if (pct > 5) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (pct < -5) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getTrendColor = (pct) => {
    if (pct > 5) return 'text-green-600';
    if (pct < -5) return 'text-red-600';
    return 'text-gray-500';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-6 pt-16 md:pt-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-chivo font-extrabold flex items-center gap-3" data-testid="sales-comparison-title">
                <BarChart3 className="w-8 h-8 text-accent" />
                Comparativo de Ventas
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                Análisis mensual para estrategias de negocio
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { fetchSummary(); fetchComparison(); fetchProductTrends(); }}
              disabled={loading}
              data-testid="refresh-comparison"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-6 bg-white rounded-xl p-1.5 shadow-sm w-fit">
            {[
              { id: 'overview', label: 'Resumen', icon: Target },
              { id: 'compare', label: 'Comparar', icon: BarChart3 },
              { id: 'products', label: 'Productos', icon: Package }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                data-testid={`tab-${id}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  activeTab === id
                    ? 'bg-accent text-white shadow-md'
                    : 'text-muted-foreground hover:text-foreground hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {loading && !summaryData ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <RefreshCw className="w-10 h-10 animate-spin text-accent mx-auto mb-3" />
                <p className="text-muted-foreground">Cargando datos...</p>
              </div>
            </div>
          ) : (
            <>
              {/* ============ OVERVIEW TAB ============ */}
              {activeTab === 'overview' && summaryData && (
                <div className="space-y-6">
                  {/* Summary Cards - Last 3 Months */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {summaryData.months.slice(0, 3).map((month, idx) => (
                      <Card key={month.label} className={`shadow-sm border-0 ${idx === 0 ? 'ring-2 ring-accent/30' : ''}`}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg font-bold flex items-center justify-between">
                            <span>{month.month_name}</span>
                            {idx === 0 && <span className="text-xs bg-accent/10 text-accent px-2 py-1 rounded-full">Actual</span>}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div>
                              <p className="text-3xl font-extrabold text-accent">{formatCurrency(month.total_sales)}</p>
                              <div className="flex items-center gap-2 mt-1">
                                {getTrendIcon(month.growth_pct)}
                                <span className={`text-sm font-semibold ${getTrendColor(month.growth_pct)}`}>
                                  {month.growth_pct > 0 ? '+' : ''}{month.growth_pct}% vs mes anterior
                                </span>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                              <div>
                                <p className="text-xs text-muted-foreground">Órdenes</p>
                                <p className="font-bold">{month.orders_count}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Ticket Prom.</p>
                                <p className="font-bold">{formatCurrency(month.avg_ticket)}</p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Best/Worst Month Indicators */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-sm">
                      <CardContent className="p-5 flex items-center gap-4">
                        <div className="p-3 bg-green-500/10 rounded-xl">
                          <Award className="w-8 h-8 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-green-700">Mejor Mes (últimos 12)</p>
                          <p className="text-xl font-bold text-green-800">{summaryData.best_month}</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 shadow-sm">
                      <CardContent className="p-5 flex items-center gap-4">
                        <div className="p-3 bg-amber-500/10 rounded-xl">
                          <Target className="w-8 h-8 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm text-amber-700">Mes con Oportunidad</p>
                          <p className="text-xl font-bold text-amber-800">{summaryData.worst_month}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* 12-Month Trend Chart */}
                  <Card className="shadow-sm border-0">
                    <CardHeader>
                      <CardTitle className="text-lg font-bold flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-accent" />
                        Tendencia de Ventas (12 Meses)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={[...summaryData.months].reverse()}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                            <XAxis dataKey="month_name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                            <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Bar dataKey="total_sales" name="Ventas" radius={[4, 4, 0, 0]}>
                              {[...summaryData.months].reverse().map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={index === summaryData.months.length - 1 ? '#f97316' : '#10b981'} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* ALL MONTHS TABLE */}
                  <Card className="shadow-sm border-0">
                    <CardHeader>
                      <CardTitle className="text-lg font-bold flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-accent" />
                        Detalle de Todos los Meses
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-slate-50">
                              <th className="text-left p-3 font-semibold">Mes</th>
                              <th className="text-right p-3 font-semibold">Ventas</th>
                              <th className="text-right p-3 font-semibold">Órdenes</th>
                              <th className="text-right p-3 font-semibold">Ticket Prom.</th>
                              <th className="text-center p-3 font-semibold">vs Anterior</th>
                              <th className="text-center p-3 font-semibold">Tendencia</th>
                            </tr>
                          </thead>
                          <tbody>
                            {summaryData.months.map((month, idx) => (
                              <tr key={month.label} className={`border-b hover:bg-slate-50 ${idx === 0 ? 'bg-accent/5' : ''}`}>
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    {idx === 0 && <span className="w-2 h-2 bg-accent rounded-full"></span>}
                                    <span className="font-medium">{month.label}</span>
                                    {idx === 0 && <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full">Actual</span>}
                                  </div>
                                </td>
                                <td className="p-3 text-right">
                                  <span className={`font-bold ${month.total_sales > 0 ? 'text-accent' : 'text-muted-foreground'}`}>
                                    {formatCurrency(month.total_sales)}
                                  </span>
                                </td>
                                <td className="p-3 text-right font-medium">{month.orders_count}</td>
                                <td className="p-3 text-right font-medium">{formatCurrency(month.avg_ticket)}</td>
                                <td className="p-3 text-center">
                                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                                    month.growth_pct > 0 ? 'bg-green-100 text-green-700' : 
                                    month.growth_pct < 0 ? 'bg-red-100 text-red-700' : 
                                    'bg-slate-100 text-slate-600'
                                  }`}>
                                    {month.growth_pct > 0 ? '+' : ''}{month.growth_pct}%
                                  </span>
                                </td>
                                <td className="p-3 text-center">
                                  {month.growth_pct > 5 ? (
                                    <TrendingUp className="w-5 h-5 text-green-500 mx-auto" />
                                  ) : month.growth_pct < -5 ? (
                                    <TrendingDown className="w-5 h-5 text-red-500 mx-auto" />
                                  ) : (
                                    <Minus className="w-5 h-5 text-slate-400 mx-auto" />
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-slate-100 font-bold">
                              <td className="p-3">TOTAL (12 meses)</td>
                              <td className="p-3 text-right text-accent">{formatCurrency(summaryData.total_period_sales)}</td>
                              <td className="p-3 text-right">{summaryData.total_period_orders}</td>
                              <td className="p-3 text-right">{formatCurrency(summaryData.avg_monthly_sales)}</td>
                              <td className="p-3 text-center text-xs text-muted-foreground">Promedio</td>
                              <td className="p-3"></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Period Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="shadow-sm border-0 bg-white">
                      <CardContent className="p-5 text-center">
                        <DollarSign className="w-8 h-8 text-accent mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Total 12 Meses</p>
                        <p className="text-2xl font-extrabold">{formatCurrency(summaryData.total_period_sales)}</p>
                      </CardContent>
                    </Card>
                    <Card className="shadow-sm border-0 bg-white">
                      <CardContent className="p-5 text-center">
                        <ShoppingCart className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Órdenes Totales</p>
                        <p className="text-2xl font-extrabold">{summaryData.total_period_orders}</p>
                      </CardContent>
                    </Card>
                    <Card className="shadow-sm border-0 bg-white">
                      <CardContent className="p-5 text-center">
                        <Calendar className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">Promedio Mensual</p>
                        <p className="text-2xl font-extrabold">{formatCurrency(summaryData.avg_monthly_sales)}</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}

              {/* ============ COMPARE TAB ============ */}
              {activeTab === 'compare' && (
                <div className="space-y-6">
                  {/* Month Selectors */}
                  <Card className="shadow-sm border-0">
                    <CardContent className="p-4">
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Comparar:</span>
                          <select
                            value={month1.month}
                            onChange={(e) => setMonth1(prev => ({ ...prev, month: parseInt(e.target.value) }))}
                            className="border rounded-lg px-3 py-2 text-sm"
                            data-testid="month1-select"
                          >
                            {MONTHS.map(m => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                          <select
                            value={month1.year}
                            onChange={(e) => setMonth1(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                            className="border rounded-lg px-3 py-2 text-sm"
                          >
                            {getYears().map(y => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                        </div>
                        <span className="text-muted-foreground font-bold">vs</span>
                        <div className="flex items-center gap-2">
                          <select
                            value={month2.month}
                            onChange={(e) => setMonth2(prev => ({ ...prev, month: parseInt(e.target.value) }))}
                            className="border rounded-lg px-3 py-2 text-sm"
                            data-testid="month2-select"
                          >
                            {MONTHS.map(m => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                          <select
                            value={month2.year}
                            onChange={(e) => setMonth2(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                            className="border rounded-lg px-3 py-2 text-sm"
                          >
                            {getYears().map(y => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {comparisonData && (
                    <>
                      {/* Comparison Summary Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Sales Comparison */}
                        <Card className="shadow-sm border-0">
                          <CardContent className="p-5">
                            <p className="text-sm text-muted-foreground mb-3">Ventas Totales</p>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="text-center p-3 bg-accent/5 rounded-lg">
                                <p className="text-xs text-muted-foreground">{comparisonData.month1.label}</p>
                                <p className="text-xl font-bold text-accent">{formatCurrency(comparisonData.month1.total_sales)}</p>
                              </div>
                              <div className="text-center p-3 bg-slate-50 rounded-lg">
                                <p className="text-xs text-muted-foreground">{comparisonData.month2.label}</p>
                                <p className="text-xl font-bold">{formatCurrency(comparisonData.month2.total_sales)}</p>
                              </div>
                            </div>
                            <div className={`mt-3 text-center p-2 rounded-lg ${comparisonData.comparison.sales_diff_pct >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                              <span className={`text-sm font-bold ${comparisonData.comparison.sales_diff_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {comparisonData.comparison.sales_diff_pct >= 0 ? '+' : ''}{comparisonData.comparison.sales_diff_pct}%
                              </span>
                              <span className="text-xs text-muted-foreground ml-2">
                                ({formatCurrency(comparisonData.comparison.sales_diff)})
                              </span>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Orders Comparison */}
                        <Card className="shadow-sm border-0">
                          <CardContent className="p-5">
                            <p className="text-sm text-muted-foreground mb-3">Número de Órdenes</p>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="text-center p-3 bg-blue-50 rounded-lg">
                                <p className="text-xs text-muted-foreground">{comparisonData.month1.label}</p>
                                <p className="text-xl font-bold text-blue-600">{comparisonData.month1.orders_count}</p>
                              </div>
                              <div className="text-center p-3 bg-slate-50 rounded-lg">
                                <p className="text-xs text-muted-foreground">{comparisonData.month2.label}</p>
                                <p className="text-xl font-bold">{comparisonData.month2.orders_count}</p>
                              </div>
                            </div>
                            <div className={`mt-3 text-center p-2 rounded-lg ${comparisonData.comparison.orders_diff_pct >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                              <span className={`text-sm font-bold ${comparisonData.comparison.orders_diff_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {comparisonData.comparison.orders_diff_pct >= 0 ? '+' : ''}{comparisonData.comparison.orders_diff_pct}%
                              </span>
                              <span className="text-xs text-muted-foreground ml-2">
                                ({comparisonData.comparison.orders_diff > 0 ? '+' : ''}{comparisonData.comparison.orders_diff})
                              </span>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Ticket Comparison */}
                        <Card className="shadow-sm border-0">
                          <CardContent className="p-5">
                            <p className="text-sm text-muted-foreground mb-3">Ticket Promedio</p>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="text-center p-3 bg-purple-50 rounded-lg">
                                <p className="text-xs text-muted-foreground">{comparisonData.month1.label}</p>
                                <p className="text-xl font-bold text-purple-600">{formatCurrency(comparisonData.month1.avg_ticket)}</p>
                              </div>
                              <div className="text-center p-3 bg-slate-50 rounded-lg">
                                <p className="text-xs text-muted-foreground">{comparisonData.month2.label}</p>
                                <p className="text-xl font-bold">{formatCurrency(comparisonData.month2.avg_ticket)}</p>
                              </div>
                            </div>
                            <div className={`mt-3 text-center p-2 rounded-lg ${comparisonData.comparison.ticket_diff_pct >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                              <span className={`text-sm font-bold ${comparisonData.comparison.ticket_diff_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {comparisonData.comparison.ticket_diff_pct >= 0 ? '+' : ''}{comparisonData.comparison.ticket_diff_pct}%
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Insights */}
                      {comparisonData.insights && comparisonData.insights.length > 0 && (
                        <Card className="shadow-sm border-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-lg font-bold flex items-center gap-2">
                              <Lightbulb className="w-5 h-5 text-indigo-600" />
                              Insights Automáticos
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {comparisonData.insights.map((insight, idx) => (
                              <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg ${
                                insight.type === 'positive' ? 'bg-green-100/50' : 
                                insight.type === 'negative' ? 'bg-red-100/50' : 
                                insight.type === 'warning' ? 'bg-amber-100/50' : 'bg-blue-100/50'
                              }`}>
                                {insight.type === 'positive' ? <TrendingUp className="w-5 h-5 text-green-600 mt-0.5" /> :
                                 insight.type === 'negative' ? <TrendingDown className="w-5 h-5 text-red-600 mt-0.5" /> :
                                 insight.type === 'warning' ? <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" /> :
                                 <Star className="w-5 h-5 text-blue-600 mt-0.5" />}
                                <p className="text-sm font-medium">{insight.message}</p>
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      )}

                      {/* Products Winners/Losers */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Winners */}
                        <Card className="shadow-sm border-0">
                          <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-t-lg">
                            <CardTitle className="text-lg font-bold flex items-center gap-2">
                              <ArrowUpRight className="w-5 h-5" />
                              Productos en Alza
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-4 space-y-2">
                            {comparisonData.products.winners.length === 0 ? (
                              <p className="text-center text-muted-foreground py-4">Sin productos en alza</p>
                            ) : (
                              comparisonData.products.winners.map((product, idx) => (
                                <div key={product.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                      {idx + 1}
                                    </span>
                                    <span className="font-medium text-sm truncate max-w-[150px]">{product.name}</span>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm font-bold text-green-600">+{formatCurrency(product.revenue_diff)}</p>
                                    <p className="text-xs text-green-700">+{product.revenue_diff_pct}%</p>
                                  </div>
                                </div>
                              ))
                            )}
                          </CardContent>
                        </Card>

                        {/* Losers */}
                        <Card className="shadow-sm border-0">
                          <CardHeader className="bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-t-lg">
                            <CardTitle className="text-lg font-bold flex items-center gap-2">
                              <ArrowDownRight className="w-5 h-5" />
                              Productos en Baja
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-4 space-y-2">
                            {comparisonData.products.losers.length === 0 ? (
                              <p className="text-center text-muted-foreground py-4">Sin productos en baja</p>
                            ) : (
                              comparisonData.products.losers.map((product, idx) => (
                                <div key={product.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <span className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                      {idx + 1}
                                    </span>
                                    <span className="font-medium text-sm truncate max-w-[150px]">{product.name}</span>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm font-bold text-red-600">{formatCurrency(product.revenue_diff)}</p>
                                    <p className="text-xs text-red-700">{product.revenue_diff_pct}%</p>
                                  </div>
                                </div>
                              ))
                            )}
                          </CardContent>
                        </Card>
                      </div>

                      {/* Day of Week Comparison */}
                      <Card className="shadow-sm border-0">
                        <CardHeader>
                          <CardTitle className="text-lg font-bold flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-accent" />
                            Ventas por Día de la Semana
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={comparisonData.by_day}>
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                                <XAxis dataKey="day_name" tick={{ fontSize: 10 }} />
                                <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                <Legend />
                                <Bar dataKey="month1_sales" name={comparisonData.month1.label} fill="#f97316" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="month2_sales" name={comparisonData.month2.label} fill="#94a3b8" radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </div>
              )}

              {/* ============ PRODUCTS TAB ============ */}
              {activeTab === 'products' && productTrends && (
                <div className="space-y-6">
                  {/* Product Categories */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="shadow-sm border-0 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <TrendingUp className="w-6 h-6 text-green-600" />
                          <h3 className="font-bold text-green-800">Productos Estrella</h3>
                        </div>
                        <p className="text-3xl font-extrabold text-green-700">{productTrends.rising_stars.length}</p>
                        <p className="text-xs text-green-600 mt-1">En crecimiento los últimos 6 meses</p>
                      </CardContent>
                    </Card>
                    <Card className="shadow-sm border-0 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <Minus className="w-6 h-6 text-blue-600" />
                          <h3 className="font-bold text-blue-800">Productos Estables</h3>
                        </div>
                        <p className="text-3xl font-extrabold text-blue-700">{productTrends.consistent_performers.length}</p>
                        <p className="text-xs text-blue-600 mt-1">Rendimiento constante</p>
                      </CardContent>
                    </Card>
                    <Card className="shadow-sm border-0 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                      <CardContent className="p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <TrendingDown className="w-6 h-6 text-amber-600" />
                          <h3 className="font-bold text-amber-800">Necesitan Atención</h3>
                        </div>
                        <p className="text-3xl font-extrabold text-amber-700">{productTrends.declining.length}</p>
                        <p className="text-xs text-amber-600 mt-1">En declive - considera promoción</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Top Products Table */}
                  <Card className="shadow-sm border-0">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="text-lg font-bold flex items-center gap-2">
                        <Award className="w-5 h-5 text-accent" />
                        Ranking de Productos (6 Meses)
                      </CardTitle>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setExpandedProducts(!expandedProducts)}
                      >
                        {expandedProducts ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        {expandedProducts ? 'Ver menos' : 'Ver más'}
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-3 font-semibold">#</th>
                              <th className="text-left p-3 font-semibold">Producto</th>
                              <th className="text-right p-3 font-semibold">Ingresos</th>
                              <th className="text-right p-3 font-semibold">Cantidad</th>
                              <th className="text-center p-3 font-semibold">Tendencia</th>
                            </tr>
                          </thead>
                          <tbody>
                            {productTrends.top_products.slice(0, expandedProducts ? 15 : 5).map((product, idx) => (
                              <tr key={product.id} className="border-b hover:bg-slate-50">
                                <td className="p-3">
                                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                    idx < 3 ? 'bg-accent text-white' : 'bg-slate-200'
                                  }`}>
                                    {idx + 1}
                                  </span>
                                </td>
                                <td className="p-3 font-medium">{product.name}</td>
                                <td className="p-3 text-right font-bold text-accent">{formatCurrency(product.total_revenue)}</td>
                                <td className="p-3 text-right">{product.total_quantity}</td>
                                <td className="p-3 text-center">
                                  {product.trend_direction === 'up' ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                                      <TrendingUp className="w-3 h-3" /> Subiendo
                                    </span>
                                  ) : product.trend_direction === 'down' ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
                                      <TrendingDown className="w-3 h-3" /> Bajando
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-semibold">
                                      <Minus className="w-3 h-3" /> Estable
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Rising Stars Detail */}
                  {productTrends.rising_stars.length > 0 && (
                    <Card className="shadow-sm border-0">
                      <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-t-lg">
                        <CardTitle className="text-lg font-bold flex items-center gap-2">
                          <Star className="w-5 h-5" />
                          Productos Estrella - En Crecimiento
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {productTrends.rising_stars.slice(0, 6).map((product) => (
                            <div key={product.id} className="p-4 bg-green-50 rounded-lg">
                              <p className="font-bold truncate">{product.name}</p>
                              <p className="text-2xl font-extrabold text-green-600 mt-1">
                                {formatCurrency(product.total_revenue)}
                              </p>
                              <p className="text-xs text-green-700 mt-1">
                                {product.total_quantity} unidades vendidas
                              </p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Declining Products - Recommendations */}
                  {productTrends.declining.length > 0 && (
                    <Card className="shadow-sm border-0">
                      <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-t-lg">
                        <CardTitle className="text-lg font-bold flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5" />
                          Productos que Necesitan Atención
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          {productTrends.declining.slice(0, 5).map((product) => (
                            <div key={product.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                              <div>
                                <p className="font-bold">{product.name}</p>
                                <p className="text-xs text-amber-700">
                                  Total: {formatCurrency(product.total_revenue)} | {product.total_quantity} uds
                                </p>
                              </div>
                              <div className="text-right">
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-200 text-amber-800 rounded-full text-xs font-semibold">
                                  <Lightbulb className="w-3 h-3" />
                                  Promocionar
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default SalesComparison;
